import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Add, ModelTraining, Delete, Close, ExpandLess, PlayArrow, CheckCircle, Refresh, PowerSettingsNew } from '@mui/icons-material';
import Plot from 'react-plotly.js';
import { useParams } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useModel } from '../contexts/ModelContext';
import { useData } from '../contexts/DataContext';
import MainLayout from '../components/Layout/MainLayout';

const parsePlotJson = (plotData) => {
  if (!plotData) return null;
  try {
    return typeof plotData === 'string' ? JSON.parse(plotData) : plotData;
  } catch {
    return null;
  }
};

const AIModels = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const { labels, fetchLabels, sensors, fetchSensors } = useProject();
  const { models, fetchModels, trainModel, deleteModel, fetchModelInfo, predictSamples, acceptPredictions, retrainModel, toggleModelActive } = useModel();
  const { samples, fetchSamples } = useData();
  const [selectedLabel, setSelectedLabel] = useState('');
  const [selectedSensor, setSelectedSensor] = useState('');
  const [modelType, setModelType] = useState('linear');
  const [dataSource, setDataSource] = useState('Labeled');
  const [training, setTraining] = useState(false);
  const [detailModel, setDetailModel] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showTraining, setShowTraining] = useState(false);

  // Prediction state
  const [predictMode, setPredictMode] = useState(false);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [predicting, setPredicting] = useState(false);
  const [predictions, setPredictions] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [retraining, setRetraining] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchLabels(projectId);
      fetchSensors(projectId);
      fetchSamples(projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (selectedLabel) {
      fetchModels(selectedLabel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLabel]);

  const handleTrainModel = async () => {
    if (!selectedLabel || !selectedSensor) return;
    setTraining(true);
    try {
      await trainModel(selectedLabel, dataSource, modelType, selectedSensor);
    } catch (error) {
      console.error('Training failed:', error);
    } finally {
      // Always refresh — model may have been created even if the request timed out
      if (selectedLabel) {
        try { await fetchModels(selectedLabel); } catch {}
      }
      setTraining(false);
    }
  };

  const handleDeleteModel = async (modelId) => {
    if (window.confirm('Are you sure you want to delete this model?')) {
      await deleteModel(modelId);
      if (selectedLabel) {
        await fetchModels(selectedLabel);
      }
      if (detailModel?.id === modelId) {
        setDetailModel(null);
      }
    }
  };

  const handleOpenDetail = async (model) => {
    setLoadingDetail(true);
    setPredictMode(false);
    setPredictions(null);
    setSelectedSamples([]);
    try {
      const fullModel = await fetchModelInfo(model.id);
      setDetailModel(fullModel);
    } catch (error) {
      console.error('Failed to load model details:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handlePredict = async () => {
    if (!detailModel || selectedSamples.length === 0) return;
    setPredicting(true);
    try {
      const result = await predictSamples(detailModel.id, selectedSamples);
      setPredictions(result);
    } catch (error) {
      console.error('Prediction failed:', error);
    } finally {
      setPredicting(false);
    }
  };

  const handleAcceptPredictions = async () => {
    if (!detailModel || !predictions || !Array.isArray(predictions)) return;
    setAccepting(true);
    try {
      await acceptPredictions(detailModel.id, predictions);
      setPredictions(null);
      setSelectedSamples([]);
      setPredictMode(false);
      // Refresh samples so accepted ones disappear from the list
      if (projectId) fetchSamples(projectId);
    } catch (error) {
      console.error('Accept predictions failed:', error);
    } finally {
      setAccepting(false);
    }
  };

  const handleRetrain = async () => {
    if (!detailModel) return;
    setRetraining(true);
    try {
      const newModel = await retrainModel(detailModel.id);
      if (selectedLabel) await fetchModels(selectedLabel);
      if (newModel?.id) {
        const fullModel = await fetchModelInfo(newModel.id);
        setDetailModel(fullModel);
      }
    } catch (error) {
      console.error('Retrain failed:', error);
    } finally {
      setRetraining(false);
    }
  };

  const handleToggleActive = async () => {
    if (!detailModel) return;
    try {
      const updated = await toggleModelActive(detailModel.id);
      setDetailModel(prev => ({ ...prev, is_active: updated.is_active }));
      if (selectedLabel) await fetchModels(selectedLabel);
    } catch (error) {
      console.error('Toggle active failed:', error);
    }
  };

  const toggleSampleSelection = (sampleId) => {
    setSelectedSamples(prev =>
      prev.includes(sampleId) ? prev.filter(id => id !== sampleId) : [...prev, sampleId]
    );
  };

  const selectAllSamples = (sampleList) => {
    const allIds = sampleList.map(s => s.id);
    const allSelected = allIds.every(id => selectedSamples.includes(id));
    if (allSelected) {
      setSelectedSamples(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedSamples(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  // Samples available for prediction: Parked + Labeled, excluding those used in training
  const availableSamples = useMemo(() => {
    if (!detailModel) return [];
    const sensorId = typeof detailModel.sensor === 'object' ? detailModel.sensor?.id : detailModel.sensor;
    const trainIds = new Set(detailModel.training_sample_ids || []);
    return samples.filter(s =>
      (s.status === 'Parked' || s.status === 'Labeled') &&
      (!sensorId || s.sensor === sensorId) &&
      !trainIds.has(s.id)
    );
  }, [samples, detailModel]);

  const parsedCV = useMemo(() => parsePlotJson(detailModel?.cv_plotly_json), [detailModel?.cv_plotly_json]);
  const parsedTest = useMemo(() => parsePlotJson(detailModel?.test_plotly_json), [detailModel?.test_plotly_json]);

  const plotLayout = (parsed) => ({
    ...parsed?.layout,
    autosize: true,
    width: undefined,
    height: 500,
    title: { text: '' },
    plot_bgcolor: 'rgba(0,0,0,0)',
    paper_bgcolor: 'rgba(0,0,0,0)',
    margin: { l: 120, r: 40, t: 40, b: 120 },
    xaxis: { ...parsed?.layout?.xaxis, side: 'bottom' },
  });

  const detailScores = detailModel?.scores;
  const isRegression = detailScores && (detailScores.calibration?.r2 !== undefined || detailScores.calibration?.rmse !== undefined);

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            AI Models
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Train and manage machine learning models
          </Typography>
        </Box>

        {/* Label Selector */}
        <Card sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Select Label"
                  value={selectedLabel}
                  onChange={(e) => setSelectedLabel(e.target.value)}
                  disabled={labels.length === 0}
                  helperText={labels.length === 0 ? 'No labels configured' : 'Select a label to view its models'}
                >
                  {labels.map((label) => (
                    <MenuItem key={label.id} value={label.id}>
                      {label.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant={showTraining ? 'outlined' : 'contained'}
                    startIcon={showTraining ? <ExpandLess /> : <Add />}
                    onClick={() => setShowTraining(!showTraining)}
                    disabled={!selectedLabel}
                  >
                    {showTraining ? 'Hide Training' : 'Train New Model'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Train New Model (collapsible) */}
        <Collapse in={showTraining && !!selectedLabel}>
          <Card sx={{ mb: 4, borderRadius: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <ModelTraining sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    Train New Model
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Training for: {labels.find((l) => l.id === selectedLabel)?.name}
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField select fullWidth label="Select Sensor" value={selectedSensor}
                    onChange={(e) => setSelectedSensor(e.target.value)} disabled={sensors.length === 0}>
                    {sensors.map((sensor) => (
                      <MenuItem key={sensor.id} value={sensor.id}>{sensor.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField select fullWidth label="Model Type" value={modelType}
                    onChange={(e) => setModelType(e.target.value)}>
                    <MenuItem value="linear">Linear</MenuItem>
                    <MenuItem value="non-linear">Non-Linear</MenuItem>
                    <MenuItem value="tabnet">TabNet</MenuItem>
                    <MenuItem value="1dcnn">1D CNN</MenuItem>
                    <MenuItem value="multiblock">Multi-Block CNN</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField select fullWidth label="Data Source" value={dataSource}
                    onChange={(e) => setDataSource(e.target.value)}>
                    <MenuItem value="Labeled">Labeled</MenuItem>
                    <MenuItem value="Cortex">Cortex</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button fullWidth variant="contained" size="large" startIcon={<Add />}
                    onClick={handleTrainModel} disabled={!selectedSensor || training} sx={{ py: 1.8 }}>
                    {training ? 'Training...' : 'Train Model'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>

        {/* Existing Models */}
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Trained Models
        </Typography>

        {!selectedLabel ? (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a Label
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a label above to view its trained models
              </Typography>
            </CardContent>
          </Card>
        ) : models.length === 0 ? (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Models Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Train your first model to get started with predictions
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {models.map((model) => (
              <Grid item xs={12} sm={6} md={4} key={model.id}>
                <Card
                  onClick={() => handleOpenDetail(model)}
                  sx={{
                    borderRadius: 3, cursor: 'pointer', transition: 'all 0.3s',
                    border: model.is_active ? `2px solid ${theme.palette.success.main}` : '2px solid transparent',
                    opacity: model.is_active === false ? 0.7 : 1,
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: theme.shadows[8] },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip label={model.sensor || 'Model'} color="primary" size="small" />
                        <Chip label={`v${model.version || 1}`} size="small" variant="outlined" />
                        <Chip
                          label={model.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={model.is_active ? 'success' : 'default'}
                          variant={model.is_active ? 'filled' : 'outlined'}
                        />
                      </Box>
                      <IconButton size="small" color="error"
                        onClick={(e) => { e.stopPropagation(); handleDeleteModel(model.id); }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {model.name || `Model #${model.id}`}
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {model.number_samples && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Samples:</strong> {model.number_samples} ({model.number_train_samples} / {model.number_test_samples})
                        </Typography>
                      )}
                      {model.scores?.calibration?.accuracy !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>CV Acc:</strong> {(model.scores.calibration.accuracy * 100).toFixed(1)}%
                        </Typography>
                      )}
                      {model.scores?.prediction?.accuracy !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Test Acc:</strong> {(model.scores.prediction.accuracy * 100).toFixed(1)}%
                        </Typography>
                      )}
                      {model.scores?.calibration?.r2 !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>CV R2:</strong> {model.scores.calibration.r2.toFixed(3)}
                        </Typography>
                      )}
                      {model.scores?.prediction?.r2 !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Test R2:</strong> {model.scores.prediction.r2.toFixed(3)}
                        </Typography>
                      )}
                      {model.created_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          {new Date(model.created_at).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Model Detail Dialog */}
        <Dialog open={!!detailModel} onClose={() => setDetailModel(null)} maxWidth="md" fullWidth
          PaperProps={{ sx: { maxHeight: '90vh' } }}>
          {detailModel && (
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h5" fontWeight={600}>
                      {detailModel.name || `Model #${detailModel.id}`}
                    </Typography>
                    <Chip label={`v${detailModel.version || 1}`} size="small" variant="outlined" />
                    <Chip
                      label={detailModel.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={detailModel.is_active ? 'success' : 'default'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Chip label={`${detailModel.number_samples} samples`} variant="outlined" size="small" />
                    <Chip label={`${detailModel.number_train_samples} train / ${detailModel.number_test_samples} test`} variant="outlined" size="small" />
                  </Box>
                </Box>
                <IconButton onClick={() => setDetailModel(null)}><Close /></IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {/* Scores */}
                {detailScores && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Performance Metrics
                    </Typography>
                    <Grid container spacing={2}>
                      {Object.entries(detailScores).map(([stage, metrics]) => (
                        <Grid item xs={12} sm={4} key={stage}>
                          <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'capitalize', mb: 1 }}>
                              {stage}
                            </Typography>
                            {Object.entries(metrics).map(([key, value]) => (
                              <Typography variant="body2" color="text.secondary" key={key}>
                                <strong>{key.toUpperCase()}:</strong>{' '}
                                {typeof value === 'number' ? (
                                  key === 'accuracy' || key === 'f1_score'
                                    ? `${(value * 100).toFixed(1)}%`
                                    : value.toFixed(4)
                                ) : String(value)}
                              </Typography>
                            ))}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Plots - stacked vertically, full width */}
                {parsedCV && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {parsedCV.layout?.title?.text || parsedCV.layout?.title || (isRegression ? 'Cross-Validation Regression' : 'Cross-Validation Confusion Matrix')}
                    </Typography>
                    <Plot
                      data={parsedCV.data}
                      layout={plotLayout(parsedCV)}
                      useResizeHandler
                      config={{ responsive: true, displayModeBar: false, displaylogo: false }}
                      style={{ width: '100%', height: 500 }}
                    />
                  </Box>
                )}
                {parsedTest && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {parsedTest.layout?.title?.text || parsedTest.layout?.title || (isRegression ? 'Test Set Regression' : 'Test Set Confusion Matrix')}
                    </Typography>
                    <Plot
                      data={parsedTest.data}
                      layout={plotLayout(parsedTest)}
                      useResizeHandler
                      config={{ responsive: true, displayModeBar: false, displaylogo: false }}
                      style={{ width: '100%', height: 500 }}
                    />
                  </Box>
                )}

                {/* Hyperparameters */}
                {detailModel.hyperparameters && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Hyperparameters
                    </Typography>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                      <Grid container spacing={1}>
                        {Object.entries(detailModel.hyperparameters).map(([key, value]) => (
                          <Grid item xs={6} sm={4} md={3} key={key}>
                            <Typography variant="caption" color="text.secondary">{key}</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Box>
                )}

                {/* Predict Section */}
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      Predict on Samples
                    </Typography>
                    <Button
                      variant={predictMode ? 'outlined' : 'contained'}
                      size="small"
                      startIcon={predictMode ? <ExpandLess /> : <PlayArrow />}
                      onClick={() => { setPredictMode(!predictMode); setPredictions(null); setSelectedSamples([]); }}
                    >
                      {predictMode ? 'Hide' : 'Select Samples'}
                    </Button>
                  </Box>

                  <Collapse in={predictMode}>
                    {availableSamples.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No samples available for prediction.
                      </Typography>
                    ) : (
                      <>
                        <Paper sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    indeterminate={selectedSamples.length > 0 && selectedSamples.length < availableSamples.length}
                                    checked={availableSamples.length > 0 && availableSamples.every(s => selectedSamples.includes(s.id))}
                                    onChange={() => selectAllSamples(availableSamples)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {availableSamples.map((sample) => (
                                <TableRow key={sample.id} hover onClick={() => toggleSampleSelection(sample.id)}
                                  sx={{ cursor: 'pointer' }}>
                                  <TableCell padding="checkbox">
                                    <Checkbox checked={selectedSamples.includes(sample.id)} size="small" />
                                  </TableCell>
                                  <TableCell>{sample.name}</TableCell>
                                  <TableCell>
                                    <Chip label={sample.status} size="small" variant="outlined" />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Paper>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Button
                            variant="contained"
                            startIcon={predicting ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
                            onClick={handlePredict}
                            disabled={selectedSamples.length === 0 || predicting}
                          >
                            {predicting ? 'Predicting...' : `Predict (${selectedSamples.length} samples)`}
                          </Button>
                          <Typography variant="body2" color="text.secondary">
                            {selectedSamples.length} of {availableSamples.length} selected
                          </Typography>
                        </Box>

                        {/* Prediction Results */}
                        {predictions && (
                          <>
                            <Paper sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Sample</TableCell>
                                    <TableCell>Prediction</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(Array.isArray(predictions) ? predictions : []).map((pred, i) => (
                                    <TableRow key={i}>
                                      <TableCell>{pred.sample_name || pred.sample_id}</TableCell>
                                      <TableCell>
                                        <Chip
                                          label={typeof pred.prediction === 'number'
                                            ? pred.prediction.toFixed(3)
                                            : String(pred.prediction)}
                                          color="primary"
                                          size="small"
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Paper>
                            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={accepting ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                                onClick={handleAcceptPredictions}
                                disabled={accepting}
                              >
                                {accepting ? 'Accepting...' : 'Accept & Move to Cortex'}
                              </Button>
                              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                Accepted samples will be available for retraining
                              </Typography>
                            </Box>
                          </>
                        )}
                      </>
                    )}
                  </Collapse>
                </Box>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={retraining ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                    onClick={handleRetrain}
                    disabled={retraining}
                  >
                    {retraining ? 'Retraining...' : 'Retrain with Cortex Data'}
                  </Button>
                  <Button
                    variant={detailModel.is_active ? 'outlined' : 'contained'}
                    color={detailModel.is_active ? 'warning' : 'success'}
                    startIcon={<PowerSettingsNew />}
                    onClick={handleToggleActive}
                  >
                    {detailModel.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </Box>
                <Button onClick={() => setDetailModel(null)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default AIModels;
